import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Card, Table } from 'antd';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { KeyValueDisplay } from '../key_value_display/KeyValueDisplay';
import { groupBy } from 'lodash';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { connect } from 'react-redux';
import { ProtectedImageThumbnails } from '../protected_image_thumbnails/ProtectedImageThumbnails';
import DateTimeHover from '../date_time_component/DateTime';
import { useBatchedState } from '../../hooks/useBatchedState';

const sanitizeTaskAnswerTitle = (groupedAnswers) =>
	Object.keys(groupedAnswers).reduce((acc, key) => {
		acc[key] = (groupedAnswers[key] || []).map((answer, idx) =>
			answer.titleId ? answer : { ...answer, titleId: `${key}-${answer.title}-${idx}` }
		);
		return acc;
	}, {});

const TaskAnswerDetails = ({ onResizeComplete, record, userType }) => {
	const photoStrings = useMemo(
		() =>
			nullSafeGetOrElse('attachments', record, []).map((p) => {
				const fileName = nullSafeGetOrElse('fileName', p, '');
				const fileId = nullSafeGetOrElse('fileId', p, '');
				return `${fileId}/${fileName}`;
			}),
		[record]
	);

	const hasCalledResizeComplete = useRef(false);

	useEffect(() => {
		if (!hasCalledResizeComplete.current && !nullSafeGet('attachments.0', record)) {
			onResizeComplete();
			hasCalledResizeComplete.current = true;
		}
	}, []);

	return (
		<div className={'taskAnswerDetails'}>
			{record.comments && record.comments.length > 0 ? (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<div className="commentsBlurb">{record.comments}</div>
				</div>
			) : null}
			{photoStrings && photoStrings.length > 0 ? (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<ProtectedImageThumbnails
						style={{ marginTop: 4, cursor: 'pointer' }}
						imageWidth={120}
						imageHeight={120}
						photoStrings={photoStrings}
						role={userType}
						resize
						allResizeComplete={onResizeComplete}
					/>
				</div>
			) : null}
		</div>
	);
};

const PrintableWalkthrough: FC<any> = ({
	walkthrough,
	userType,
	onPrintInitiate,
	onPrintStart,
}): React.ReactElement => {
	const componentRef = useRef();
	const [resizeCompleted, setResizeCompleted] = useState(false);
	const resizeCompletedRef = useRef(resizeCompleted);
	const [resizeCompletedTitleIds, queueTitleIdUpdate] = useBatchedState<any[]>([], 50);

	const onResizeComplete = useCallback(
		(titleId) => () => {
			queueTitleIdUpdate((existingIds) => {
				if (existingIds.includes(titleId)) return existingIds;
				return [...existingIds, titleId];
			});
		},
		[queueTitleIdUpdate]
	);

	const groupedFilteredTaskAnswers =
		walkthrough.taskAnswers &&
		sanitizeTaskAnswerTitle(groupBy(walkthrough.taskAnswers, 'groupTitle'));

	const allTitleIds = useMemo(
		() =>
			Object.keys(groupedFilteredTaskAnswers).reduce((acc, key) => {
				return [...acc, ...(groupedFilteredTaskAnswers[key] || []).map((_) => _.titleId)];
			}, []),
		[groupedFilteredTaskAnswers]
	);

	useEffect(() => {
		resizeCompletedRef.current = resizeCompleted;
	}, [resizeCompleted]);

	useEffect(() => {
		allTitleIds.every((_) => resizeCompletedTitleIds.includes(_)) && setResizeCompleted(true);
	}, [allTitleIds, resizeCompletedTitleIds]);

	const walkthroughDetails = useMemo(
		() => ({
			'Walkthrough ID': `# ${nullSafeGet('id', walkthrough)}`,
			'Site Survey': nullSafeGet('siteSurvey.title', walkthrough),
			Location: nullSafeGet('location.name', walkthrough),
			Created: (
				<DateTimeHover
					showDate
					timestamp={walkthrough.createdAt}
					timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
				/>
			),
			Due: walkthrough.dueAt ? (
				<DateTimeHover
					showDate
					timestamp={walkthrough.dueAt}
					timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
				/>
			) : null,
			...(walkthrough.status === 'complete' && walkthrough.completedAt
				? {
						Completed: (
							<DateTimeHover
								showDate
								timestamp={walkthrough.completedAt}
								timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
							/>
						),
				  }
				: {}),
			...(walkthrough.completedByContact
				? {
						'Completed By': `${nullSafeGetOrElse(
							'completedByContact.nameGiven',
							walkthrough,
							''
						)} ${nullSafeGetOrElse('completedByContact.nameFamily', walkthrough, '')}`,
				  }
				: {}),
		}),
		[walkthrough]
	);

	const handlePrint = useReactToPrint({
		content: () => componentRef.current,
		onBeforePrint: onPrintStart,
		documentTitle: `Openwrench - Walkthrough:#${walkthrough.id} `,
	});

	// Maximum wait time for images to load (30 seconds)
	const MAX_PRINT_WAIT_ATTEMPTS = 100; // 100 * 300ms = 30 seconds

	const initiatePrint = (times = 0) => {
		if (resizeCompletedRef.current || times >= MAX_PRINT_WAIT_ATTEMPTS) {
			// Proceed with print - either all images loaded or timeout reached
			handlePrint();
		} else {
			setTimeout(() => {
				initiatePrint(times + 1);
			}, 300);
		}
	};

	const startPrint = () => {
		onPrintInitiate();
		initiatePrint();
	};

	const filteredWalkthroughDetails = useMemo(
		() =>
			Object.keys(walkthroughDetails).reduce((acc, el) => {
				const val = walkthroughDetails[el];
				if (val !== null && val !== 'undefined' && val !== undefined && val !== '') {
					acc[el] = walkthroughDetails[el];
				}
				return acc;
			}, {}),
		[walkthroughDetails]
	);

	const walkthroughColumns = useMemo(
		() => [
			{
				title: 'Question',
				dataIndex: 'task',
				render: (text, record) => (
					<div className="flex flex-col">
						<div>
							<div className="walkthroughRowDisplay__rowTitle text-gray-500">
								<Ellipsis
									style={{ wordBreak: 'normal' }}
									tooltip={true}
									length={64}
									className="text-xl"
								>
									{nullSafeGet('title', record)}
								</Ellipsis>
							</div>
							{nullSafeGet('description', record) && (
								<div className="walkthroughRowDisplay__rowSubtitle text-gray-400">
									{record.description}
								</div>
							)}
							{nullSafeGet('questionType', record) !== 'multipleChoice' && (
								<div className="walkthroughRowDisplay__rowTitle mt-2 text-gray-600">
									<div className="walkthroughRowDisplay__rowInlineGroup">
										{nullSafeGetOrElse('answer', record, '--')}
									</div>
								</div>
							)}
						</div>
						<TaskAnswerDetails
							userType={userType}
							record={record}
							onResizeComplete={onResizeComplete(record.titleId)}
						/>
					</div>
				),
			},
			{
				title: 'Answer',
				dataIndex: 'task',
				render: (text, record) => (
					<div className="flex flex-row items-center justify-end">
						{nullSafeGet('questionType', record) === 'multipleChoice' && (
							<div className="walkthroughRowDisplay__rowSubtitle ml-2 text-gray-600">
								<div className="walkthroughRowDisplay__rowInlineGroup text-sm">
									{nullSafeGetOrElse('answer', record, '--')}
								</div>
							</div>
						)}
					</div>
				),
			},
		],
		[userType]
	);

	return (
		<div>
			<div onClick={startPrint}>Export PDF</div>

			<div className=" hidden h-0 overflow-hidden" id="printdiv">
				<div id="printable-walkthrough" ref={componentRef}>
					<Card title="Walkthrough Details" className="rowSpacing">
						<div style={{ padding: 0 }} className="materialCard__body">
							<div className="materialCard__supportingText">
								<KeyValueDisplay
									horizontal={true}
									keyWidth={120}
									keyValueStore={filteredWalkthroughDetails}
								/>
							</div>
						</div>
					</Card>
					<div>
						{walkthrough.taskAnswers &&
						walkthrough.taskAnswers.filter((t) => t.answer).length > 0 ? (
							Object.keys(groupedFilteredTaskAnswers).map((gtaKey) => {
								return (
									<div key={gtaKey} className="rowSpacing">
										<Card title={gtaKey !== 'undefined' ? gtaKey : null}>
											<Table
												showHeader={false}
												pagination={false}
												columns={walkthroughColumns}
												rowKey={(el) => el.titleId}
												dataSource={groupedFilteredTaskAnswers[gtaKey]}
											/>
										</Card>
									</div>
								);
							})
						) : (
							<div style={{ maxWidth: 488, textAlign: 'center' }}>
								Walkthrough has not been conducted yet
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(PrintableWalkthrough);
